import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {Season, SeasonCreateRequest} from "@/api/client/models/index.ts";
import {useApiClient} from "@/hooks/api-hook.ts";

export const useCharterSeasonsStore = defineStore('charter-seasons', () => {
  const api = useApiClient()

  // State
  const seasons = ref<Season[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const seasonsCount = computed(() => seasons.value.length);
  const hasSeasons = computed(() => seasons.value.length > 0);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => error.value !== null);

  // Actions
  async function fetchSeasons(isActive: boolean = true) {
    loading.value = true;
    error.value = null;

    try {
      seasons.value = await api.season.get({queryParameters: {isActive: isActive}})
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch seasons';
      console.error('Error fetching seasons:', err);
    } finally {
      loading.value = false;
    }
  }

  async function fetchSeasonsForTourOperator(isActive: boolean = true) {
    loading.value = true;
    error.value = null;

    try {
      seasons.value = await api.season.getForTourOperator.get({queryParameters: {isActive}});
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch seasons for tour operator';
      console.error('Error fetching seasons for tour operator:', err);
    } finally {
      loading.value = false;
    }
  }

  function clearSeasons() {
    seasons.value = [];
    error.value = null;
  }

  function clearError() {
    error.value = null;
  }

  async function addSeason(seasonCreateRequest: SeasonCreateRequest) {
    loading.value = true;
    error.value = null;

    try {
      const newSeason = await api.season.post(seasonCreateRequest);
      seasons.value.push(newSeason);
      return newSeason;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add season';
      console.error('Error adding season:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    seasons,
    loading,
    error,

    // Getters
    seasonsCount,
    hasSeasons,
    isLoading,
    hasError,

    // Actions
    fetchSeasons,
    fetchSeasonsForTourOperator,
    clearSeasons,
    clearError,
    addSeason
  };
});
